import { useState } from "react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import DatePicker from "@/components/DatePicker";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import ColumnFilter from "@/components/ColumnFilter";
import { useColFilter } from "@/lib/tableFilter";
import { Search, FileSpreadsheet, FileText, X, Wallet } from "lucide-react";

const TRCOLS = [
  { key: "borclu", label: "Borçlu İsmi", type: "text" },
  { key: "tarih", label: "Tahsilat Tarihi", type: "date" },
  { key: "tur", label: "Tür", type: "text" },
  { key: "tutar", label: "Tahsilat Tutarı", type: "money" },
];

export default function TahsilatRaporu() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [rows, setRows] = useState([]);
  const [toplam, setToplam] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const { data } = await api.get("/records/tahsilat-rapor", { params });
      setRows(data.rows || []);
      setToplam(data.toplam || 0);
      setLoaded(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rapor yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const download = async (type) => {
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const res = await api.get(`/records/tahsilat-rapor/export/${type}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = type === "excel" ? "icra_tahsilat_raporu.xlsx" : "icra_tahsilat_raporu.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type === "excel" ? "Excel" : "PDF"} indirildi`);
    } catch (e) {
      toast.error("Dışa aktarma başarısız");
    }
  };

  const clearFilters = () => {
    setStart("");
    setEnd("");
  };

  const { colSel, setColSel, sort, setSort, view, distinct } = useColFilter(TRCOLS, rows);
  const filtreliToplam = view.reduce((s, r) => s + (r.tutar || 0), 0);

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">İCRA</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Tahsilat Raporu</h1>
          <p className="text-sm text-[#8C8377] mt-1">
            Tarih aralığına göre tüm İcra tahsilatları — borçlu, tarih ve tutar.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => download("excel")}
            disabled={!loaded || rows.length === 0}
            data-testid="tahsilat-rapor-excel"
            className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150 disabled:opacity-40"
          >
            <FileSpreadsheet className="w-4 h-4 text-[#2F6E4E]" /> Excel
          </button>
          <button
            onClick={() => download("pdf")}
            disabled={!loaded || rows.length === 0}
            data-testid="tahsilat-rapor-pdf"
            className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150 disabled:opacity-40"
          >
            <FileText className="w-4 h-4 text-[#A63A3A]" /> PDF
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E7DECC] rounded-sm p-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-52">
            <label className="kicker block mb-1.5">Başlangıç</label>
            <DatePicker value={start} onChange={setStart} placeholder="Başlangıç tarihi" testId="tahsilat-rapor-start" />
          </div>
          <div className="w-full sm:w-52">
            <label className="kicker block mb-1.5">Bitiş</label>
            <DatePicker value={end} onChange={setEnd} placeholder="Bitiş tarihi" testId="tahsilat-rapor-end" />
          </div>
          <button
            onClick={load}
            data-testid="tahsilat-rapor-search"
            className="flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150"
          >
            <Search className="w-4 h-4" /> Raporu Getir
          </button>
          {(start || end) && (
            <button
              onClick={clearFilters}
              data-testid="tahsilat-rapor-clear"
              className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"
            >
              <X className="w-4 h-4" /> Temizle
            </button>
          )}
        </div>
      </div>

      {loaded && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="tahsilat-rapor-stat-adet">
            <div className="kicker mb-1">Tahsilat Adedi</div>
            <div className="font-heading text-2xl font-bold tabular-nums">{rows.length}</div>
          </div>
          <div className="bg-white border border-[#E7DECC] rounded-sm p-4 sm:col-span-2" data-testid="tahsilat-rapor-stat-toplam">
            <div className="kicker mb-1">Toplam Tahsilat</div>
            <div className="font-heading text-2xl font-bold tabular-nums text-[#2F6E4E]">{fmt(toplam)}</div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden">
        <div className="max-h-[560px] overflow-auto">
          <table className="w-full text-sm" data-testid="tahsilat-rapor-table">
            <thead className="sticky top-0 bg-[#F3ECDD] border-b border-[#E7DECC] z-10">
              <tr>
                {TRCOLS.map((c) => (
                  <th key={c.key} className={`px-4 py-3 kicker ${c.type === "money" ? "text-right" : "text-left"}`}>
                    <div className={`flex items-center gap-1 ${c.type === "money" ? "justify-end" : "justify-between"}`}>
                      <span>{c.label}{sort.key === c.key ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}</span>
                      <ColumnFilter col={c} values={distinct(c)} selected={colSel[c.key] ?? null} onChange={(v) => setColSel((p) => ({ ...p, [c.key]: v }))} sortDir={sort.key === c.key ? sort.dir : null} onSort={(dir) => setSort({ key: c.key, dir })} onClearSort={() => setSort((s) => (s.key === c.key ? { key: null, dir: null } : s))} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-[#8C8377]">Yükleniyor…</td></tr>
              ) : !loaded ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-[#8C8377]">Tarih aralığı seçip "Raporu Getir" ile listeleyin.</td></tr>
              ) : view.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-[#8C8377]">Seçilen aralıkta tahsilat bulunamadı.</td></tr>
              ) : (
                view.map((r, i) => (
                  <tr key={i} className="border-b border-[#EFE7D6] hover:bg-[#F3ECDD]" data-testid={`tahsilat-rapor-row-${i}`}>
                    <td className="px-4 py-2.5 font-medium">{r.borclu}</td>
                    <td className="px-4 py-2.5 tabular-nums">{fmtDate(r.tarih)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-xs font-medium ${r.tur === "Faiz" ? "bg-[#F3E8FF] text-[#7C5E9B]" : "bg-[#E7F0FF] text-[#B8933F]"}`}>
                        {r.tur}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-[#2F6E4E] font-medium">{fmt(r.tutar)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {loaded && view.length > 0 && (
              <tfoot className="sticky bottom-0">
                <tr className="border-t-2 border-[#E7DECC] font-bold bg-[#F3ECDD]">
                  <td className="px-4 py-2.5" colSpan={3}>FİLTRELİ TOPLAM ({view.length})</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-[#2F6E4E]">{fmt(filtreliToplam)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </Layout>
  );
}
